import React from "react";
import { Card, Row, Col, Badge } from "react-bootstrap";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
} from "recharts";

export default function CorrelationResults({
  result,
  exposureVariable,
  outcomeVariable,
}) {
  if (!result) return null;

  const { exposure_variable_type, rho, pval, ci, explanation } = result;

  // Determine significance
  const getSignificance = (p) => {
    if (p < 0.001) return { text: "Highly Significant", variant: "success" };
    if (p < 0.01) return { text: "Very Significant", variant: "success" };
    if (p < 0.05) return { text: "Significant", variant: "info" };
    return { text: "Not Significant", variant: "danger" };
  };

  const significance = getSignificance(pval);

  // Generate mock scatter plot data based on correlation coefficient
  // In a real implementation, the backend should return the actual data points
  const generateScatterData = () => {
    const points = [];
    const n = 50; // number of points

    for (let i = 0; i < n; i++) {
      const x = Math.random() * 100;
      // Generate y with correlation approximation
      const noise = (Math.random() - 0.5) * 50 * (1 - Math.abs(rho));
      const y = x * rho + noise + 50;
      points.push({ x: Math.max(0, x), y: Math.max(0, y) });
    }

    return points;
  };

  const scatterData = generateScatterData();

  return (
    <div className="correlation-results mt-4">
      <Row className="g-3">
        {/* Statistics Card */}
        <Col xs={12} lg={5}>
          <Card className="h-100">
            <Card.Header>
              <strong>Correlation Analysis Results</strong>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="mb-2">
                  <strong>Exposure Variable:</strong>
                  <div className="text-muted">{exposureVariable}</div>
                </div>
                <div className="mb-2">
                  <strong>Outcome Variable:</strong>
                  <div className="text-muted">{outcomeVariable}</div>
                </div>
                <div className="mb-2">
                  <strong>Variable Type:</strong>
                  <div className="text-muted text-capitalize">
                    {exposure_variable_type}
                  </div>
                </div>
              </div>

              <hr />

              <div className="mb-3">
                <Row className="align-items-center mb-2">
                  <Col xs={6}>
                    <strong>Correlation (ρ):</strong>
                  </Col>
                  <Col xs={6} className="text-end">
                    <h5 className="mb-0">
                      {typeof rho === "number" ? rho.toFixed(3) : "N/A"}
                    </h5>
                  </Col>
                </Row>
              </div>

              <div className="mb-3">
                <Row className="align-items-center mb-2">
                  <Col xs={6}>
                    <strong>P-value:</strong>
                  </Col>
                  <Col xs={6} className="text-end">
                    <span>
                      {typeof pval === "number"
                        ? pval < 0.001
                          ? "< 0.001"
                          : pval.toFixed(4)
                        : "N/A"}
                    </span>
                  </Col>
                </Row>
                <Row className="align-items-center mb-2">
                  <Col xs={12}>
                    <Badge bg={significance.variant} className="w-100 py-2">
                      {significance.text}
                    </Badge>
                  </Col>
                </Row>
              </div>

              <div className="mb-3">
                <strong>Confidence Interval (95%):</strong>
                <div className="text-muted">
                  {Array.isArray(ci) && ci.length === 2
                    ? `[${ci[0].toFixed(3)}, ${ci[1].toFixed(3)}]`
                    : "N/A"}
                </div>
              </div>

              <hr />

              <div>
                <strong>Interpretation:</strong>
                <p className="text-muted mb-0 mt-2">{explanation}</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Scatter Plot */}
        <Col xs={12} lg={7}>
          <Card className="h-100">
            <Card.Header>
              <strong>Scatter Plot Visualization</strong>
            </Card.Header>
            <Card.Body className="d-flex align-items-center justify-content-center">
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart
                  margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="x"
                    name={exposureVariable}
                    tick={{ fontSize: 12 }}
                  >
                    <Label
                      value={exposureVariable}
                      position="bottom"
                      offset={20}
                      style={{ fontSize: 12, fontWeight: 600 }}
                    />
                  </XAxis>
                  <YAxis
                    type="number"
                    dataKey="y"
                    name={outcomeVariable}
                    tick={{ fontSize: 12 }}
                  >
                    <Label
                      value={outcomeVariable}
                      angle={-90}
                      position="left"
                      offset={20}
                      style={{ fontSize: 12, fontWeight: 600 }}
                    />
                  </YAxis>
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    formatter={(value) => value.toFixed(2)}
                  />
                  <Scatter
                    name="Data Points"
                    data={scatterData}
                    fill="#1E57BE"
                    fillOpacity={0.6}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </Card.Body>
            <Card.Footer className="text-muted small">
              <strong>Note:</strong> This visualization represents the
              relationship pattern. Actual data points would be provided by the
              analysis.
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
